// Staff Negotiation Workspace — /operations/negotiations/$id
import {
  buildCustomerContextRecord,
  negotiationContextFields,
} from "@/lib/customer-context";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  Handshake,
  ArrowLeft,
  UserCircle2,
  Building2,
  Phone,
  Mail,
  FileText,
  Save,
  Upload,
  PhoneCall,
  FileSignature,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  TrendingUp,
  Send,
  Sparkles,
  Loader2,
  Target,
  Gavel,
  MessageSquare,
  CircleDot,
  DollarSign,
  Bell,
  Calendar,
  StickyNote,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import { useRecoveryFacts } from "@/hooks/use-recovery-facts";
import {
  NOT_AVAILABLE,
  actualNegotiatedSavings,
  actualSavingsPct,
  resolveNegotiationFinancial,
} from "@/lib/financial/surface-financials";
import { supabase } from "@/integrations/supabase/client";
import type {
  InternalNote,
  Negotiation,
  NegotiationMessage,
  Review,
  ReviewFile,
  Profile,
  TimelineEvent,
} from "@/lib/db/models";
import { negotiationService, negotiationStages } from "@/lib/services/negotiationService";

export const Route = createFileRoute("/operations/negotiations/$id")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Negotiation Workspace — Operations" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["staff", "admin"]}>
      <WorkspacePage />
    </AuthGate>
  ),
});

const TONES = [
  { key: "aggressive", label: "Aggressive" },
  { key: "firm", label: "Firm" },
  { key: "professional", label: "Professional" },
  { key: "friendly", label: "Friendly" },
] as const;

const PRIORITIES = [
  { key: "urgent", label: "Urgent" },
  { key: "high", label: "High" },
  { key: "normal", label: "Normal" },
  { key: "low", label: "Low" },
] as const;

function WorkspacePage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [negotiation, setNegotiation] = useState<Negotiation | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [customer, setCustomer] = useState<Profile | null>(null);
  const [files, setFiles] = useState<ReviewFile[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [messages, setMessages] = useState<NegotiationMessage[]>([]);
  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outgoing, setOutgoing] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  /* SAB-001: canonical Total Supported Recovery for the linked review. */
  const linkedReviewIds = useMemo(() => (review ? [review.id] : []), [review]);
  const { facts: recoveryFacts } = useRecoveryFacts(linkedReviewIds);
  const canonicalRecovery = review ? (recoveryFacts.get(review.id)?.recovery ?? null) : null;

  async function reload() {
    const [{ data: n }] = await Promise.all([
      supabase.from("negotiations").select("*").eq("id", id).maybeSingle(),
    ]);
    if (!n) {
      setLoading(false);
      return;
    }
    const neg = n as Negotiation;
    setNegotiation(neg);
    const [r, f, t, m, notesRes] = await Promise.all([
      supabase.from("reviews").select("*").eq("id", neg.review_id).maybeSingle(),
      supabase.from("review_files").select("*").eq("review_id", neg.review_id),
      supabase
        .from("timeline_events")
        .select("*")
        .eq("review_id", neg.review_id)
        .order("created_at", { ascending: false }),
      negotiationService.listMessages(neg.id),
      supabase
        .from("internal_notes")
        .select("*")
        .eq("review_id", neg.review_id)
        .order("created_at", { ascending: false }),
    ]);
    setReview((r.data as Review) ?? null);
    setFiles((f.data ?? []) as ReviewFile[]);
    setTimeline((t.data ?? []) as TimelineEvent[]);
    setMessages(m);
    setNotes((notesRes.data ?? []) as InternalNote[]);
    if (r.data) {
      const { data: p } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", (r.data as Review).customer_id)
        .maybeSingle();
      setCustomer((p as Profile) ?? null);
    }
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const stages = useMemo(
    () => negotiationStages(negotiation, review?.status),
    [negotiation, review?.status],
  );

  if (loading) {
    return (
      <div className="min-h-dvh bg-secondary/30">
        <SiteHeader />
        <div className="container-page py-14">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading workspace…
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!negotiation || !review) {
    return (
      <div className="min-h-dvh bg-secondary/30">
        <SiteHeader />
        <div className="container-page py-14">
          <h1 className="font-display text-2xl text-navy">Negotiation not found.</h1>
          <Link
            to="/operations/negotiations"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-negotiate"
          >
            <ArrowLeft className="h-4 w-4" /> Back to queue
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const original = Number(negotiation.original_amount ?? review.total_amount ?? 0);
  const settlement = negotiation.settlement_amount ? Number(negotiation.settlement_amount) : null;
  /* SAB-001: no percentage estimates. Either ACTUAL negotiated savings (a
     recorded settlement) or the linked review's canonical Total Supported
     Recovery. Never both, never a fabricated multiplier. */
  const financial = resolveNegotiationFinancial({
    originalAmount: original || null,
    settlementAmount: settlement,
    canonicalRecovery,
  });
  const potentialSavings = financial.amount;

  async function updateNeg(patch: Partial<Negotiation>) {
    if (!negotiation) return;
    setSaving(true);
    try {
      await negotiationService.update(negotiation.id, patch);
      setNegotiation({ ...negotiation, ...patch } as Negotiation);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function transition(status: string, opts: {
    title: string;
    description?: string;
    reviewStatus?: string;
    patch?: Partial<Negotiation>;
  }) {
    if (!negotiation || !user) return;
    setBusy(status);
    try {
      await negotiationService.transitionStatus({
        id: negotiation.id,
        reviewId: negotiation.review_id,
        actorId: user.id,
        status,
        reviewStatus: opts.reviewStatus,
        title: opts.title,
        description: opts.description,
        patch: opts.patch,
      });
      toast.success(opts.title);
      await reload();
    } catch (e) {
      toast.error("Action failed", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function assignSelf() {
    if (!user || !negotiation) return;
    setBusy("assign");
    try {
      await negotiationService.assign(negotiation.id, user.id, negotiation.review_id, user.id);
      toast.success("Assigned to you");
      await reload();
    } catch (e) {
      toast.error("Assign failed", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function sendOutgoing(kind: "email_customer" | "email_vendor") {
    if (!user || !negotiation || !outgoing.trim()) return;
    setBusy(kind);
    try {
      // Log as timeline event
      await supabase.from("timeline_events").insert({
        review_id: negotiation.review_id,
        event_type: kind,
        title: kind === "email_customer" ? "Emailed customer" : "Emailed vendor",
        description: outgoing.slice(0, 200),
        actor_id: user.id,
        visible_to_customer: kind === "email_customer",
      });
      // If customer, also drop into message center
      if (kind === "email_customer") {
        await negotiationService.sendMessage({
          negotiationId: negotiation.id,
          reviewId: negotiation.review_id,
          authorId: user.id,
          senderRole: "staff",
          body: outgoing.trim(),
        });
      }
      setOutgoing("");
      toast.success(kind === "email_customer" ? "Customer notified" : "Vendor contacted");
      await reload();
    } catch (e) {
      toast.error("Send failed", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function addNote() {
    if (!user || !negotiation || !noteBody.trim()) return;
    setBusy("note");
    try {
      const { data, error } = await supabase
        .from("internal_notes")
        .insert({
          review_id: negotiation.review_id,
          author_id: user.id,
          body: noteBody.trim(),
        })
        .select()
        .single();
      if (error) throw error;
      setNotes((prev) => [data as InternalNote, ...prev]);
      setNoteBody("");
      toast.success("Note saved");
    } catch (e) {
      toast.error("Failed to save note", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function generateLetter(kind: "initial" | "followup" | "final" | "settlement" | "closure") {
    if (!negotiation || !user || !review) return;
    setBusy(`gen-${kind}`);
    const vendor = review.vendor ?? "Vendor";
    const fileNo = review.file_number;
    const templates: Record<typeof kind, string> = {
      initial: `Dear ${vendor},\n\nRe: Invoice ${fileNo}\n\nAfter a formal review of the referenced invoice, several charges require adjustment before payment can be issued. ${negotiation.strongest_argument ?? "The strongest concern is documented in the attached findings report."}\n\nWe are prepared to settle at a fair adjusted amount as outlined in the attached findings.\n\nPlease respond within 10 business days.\n\nRegards,\nClaimGuard — Negotiation Team`,
      followup: `Dear ${vendor},\n\nFollowing our earlier correspondence on invoice ${fileNo}, we have not yet received your response. Please provide an updated position within 5 business days.\n\nRegards,\nClaimGuard`,
      final: `FINAL DEMAND — ${fileNo}\n\nDear ${vendor},\n\nThis is our final written attempt to resolve the disputed charges. Absent a response within 5 business days, we will advise our client of all available remedies.\n\nRegards,\nClaimGuard — Negotiation Team`,
      settlement: `SETTLEMENT PROPOSAL — ${fileNo}\n\nWe propose settlement at $${Math.round(Number(negotiation.counter_offer ?? negotiation.estimated_target ?? original)).toLocaleString()}, in full and final resolution of all disputed line items.`,
      closure: `Dear Client,\n\nWe are pleased to close this negotiation. Please find the executed settlement documents attached. Total savings: ${savings != null ? `$${Math.round(savings).toLocaleString()}` : "to be confirmed"}.\n\nRegards,\nClaimGuard`,
    };
    try {
      await updateNeg({ letter_body: templates[kind] });
      await supabase.from("timeline_events").insert({
        review_id: negotiation.review_id,
        event_type: `letter_${kind}`,
        title: `${kind.charAt(0).toUpperCase() + kind.slice(1)} letter generated`,
        actor_id: user.id,
        visible_to_customer: kind !== "followup",
      });
      await reload();
    } finally {
      setBusy(null);
    }
  }

  const isCompleted = ["completed", "accepted", "settled"].includes(negotiation.status);
  const savings = actualNegotiatedSavings(original || null, settlement);
  const savingsPct = actualSavingsPct(original || null, settlement);

  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page space-y-6 py-8">
        <Breadcrumbs
          items={[
            { label: "Operations", to: "/operations" },
            { label: "Negotiations", to: "/operations/negotiations" },
            { label: negotiation.negotiation_number ?? id.slice(0, 8) },
          ]}
        />

        {/* Header */}
        <section className="sticky top-0 z-30 rounded-2xl border border-border bg-card/95 p-6 shadow-card backdrop-blur-md md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-negotiate-soft px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-negotiate">
                <Handshake className="h-3.5 w-3.5" /> Workspace
              </div>
              <h1 className="mt-3 font-display text-3xl text-navy md:text-4xl">
                {negotiation.negotiation_number}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" /> {review.vendor ?? "Vendor not extracted"}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <UserCircle2 className="h-4 w-4" />{" "}
                  {customer?.full_name ?? customer?.email ?? "Customer"}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <FileText className="h-4 w-4" /> {review.file_number}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Invoice" value={`$${Math.round(original).toLocaleString()}`} />
              <MiniStat
                label={financial.label}
                value={financial.value}
                positive
              />
              <MiniStat
                label="Status"
                value={<StatusPill status={negotiation.status} />}
              />
            </div>
          </div>

          {/* Action bar */}
          <div className="mt-6 flex flex-wrap gap-2 border-t border-border pt-5">
            {!negotiation.assigned_specialist_id && (
              <ActionButton
                onClick={assignSelf}
                busy={busy === "assign"}
                icon={<UserCircle2 className="h-4 w-4" />}
                primary
              >
                Assign to me
              </ActionButton>
            )}
            <ActionButton
              onClick={() =>
                transition("vendor_contacted", {
                  title: "Vendor contacted",
                  description: "Initial outreach sent to vendor.",
                  reviewStatus: "waiting_vendor",
                })
              }
              busy={busy === "vendor_contacted"}
              icon={<Mail className="h-4 w-4" />}
            >
              Email Vendor
            </ActionButton>
            <ActionButton
              onClick={() =>
                transition("negotiating", {
                  title: "Update sent to customer",
                  reviewStatus: "negotiating",
                })
              }
              busy={busy === "negotiating"}
              icon={<MessageSquare className="h-4 w-4" />}
            >
              Email Customer
            </ActionButton>
            <ActionButton
              onClick={() => toast.info("Upload UI wires into review_files bucket in V1.")}
              icon={<Upload className="h-4 w-4" />}
              variant="subtle"
            >
              Upload
            </ActionButton>
            <ActionButton
              onClick={() =>
                supabase
                  .from("timeline_events")
                  .insert({
                    review_id: negotiation.review_id,
                    event_type: "phone_call",
                    title: "Phone call logged",
                    actor_id: user?.id,
                    visible_to_customer: true,
                  })
                  .then(() => {
                    toast.success("Call logged");
                    reload();
                  })
              }
              icon={<PhoneCall className="h-4 w-4" />}
              variant="subtle"
            >
              Record Call
            </ActionButton>
            <ActionButton
              onClick={() => generateLetter("initial")}
              busy={busy === "gen-initial"}
              icon={<FileSignature className="h-4 w-4" />}
              variant="subtle"
            >
              Generate Letter
            </ActionButton>
            {!isCompleted ? (
              <ActionButton
                onClick={() => {
                  const amt = prompt("Settlement amount ($)", String(negotiation.counter_offer ?? ""));
                  if (!amt) return;
                  const n = Number(amt);
                  if (!Number.isFinite(n)) return;
                  transition("accepted", {
                    title: "Settlement recorded",
                    description: `Vendor settled at $${n.toLocaleString()}`,
                    reviewStatus: "completed",
                    patch: {
                      settlement_amount: n,
                      final_offer: n,
                      accepted_at: new Date().toISOString(),
                    },
                  });
                }}
                busy={busy === "accepted"}
                icon={<Gavel className="h-4 w-4" />}
                primary
              >
                Settlement
              </ActionButton>
            ) : (
              <ActionButton
                onClick={() =>
                  transition("completed", {
                    title: "Negotiation completed",
                    reviewStatus: "completed",
                  })
                }
                busy={busy === "completed"}
                icon={<CheckCircle2 className="h-4 w-4" />}
                primary
              >
                Complete
              </ActionButton>
            )}
          </div>

          {/* Stage strip */}
          <ol className="mt-6 flex flex-wrap gap-2">
            {stages.map((s, i) => (
              <li
                key={s.key}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                  s.current
                    ? "border-negotiate bg-negotiate-soft text-negotiate"
                    : s.reached
                      ? "border-negotiate/20 bg-card text-navy"
                      : "border-border bg-secondary/40 text-muted-foreground"
                }`}
              >
                {s.reached ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <span className="text-[10px]">{i + 1}</span>
                )}
                {s.label}
              </li>
            ))}
          </ol>
        </section>

        <CaseSummaryPanel
          negotiation={negotiation}
          review={review}
          customer={customer}
          messages={messages}
          timelineCount={timeline.length}
        />

        {/* 3-column layout */}
        <div className="grid gap-6 lg:grid-cols-[280px_1fr_320px]">
          {/* LEFT — Customer + Invoice */}
          <aside className="space-y-4">
            <Panel title="Customer">
              <ContactRow icon={<UserCircle2 className="h-4 w-4" />} label="Full Name" value={customer?.full_name} placeholder="Not on file" />
              <ContactRow icon={<Mail className="h-4 w-4" />} label="Email" value={customer?.email} placeholder="Not on file" />
              <ContactRow icon={<Phone className="h-4 w-4" />} label="Phone" value={customer?.phone} placeholder="Not provided" />
              <ContactRow icon={<FileText className="h-4 w-4" />} label="Reference" value={review.file_number} placeholder="—" />
            </Panel>

            <Panel title="Invoice">
              <ContactRow icon={<Building2 className="h-4 w-4" />} label="Vendor" value={review.vendor} placeholder="Vendor not extracted" />
              <ContactRow
                icon={<DollarSign className="h-4 w-4" />}
                label="Amount"
                value={review.total_amount != null ? `$${Number(review.total_amount).toLocaleString()}` : null}
                placeholder="Awaiting parsing"
              />
              <ContactRow icon={<Calendar className="h-4 w-4" />} label="Invoice Date" value={review.invoice_date} placeholder="Not dated" />
              <Link
                to="/report/$id"
                params={{ id: review.file_number }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-negotiate hover:underline"
              >
                <FileText className="h-3.5 w-3.5" /> Open AI Findings Report
              </Link>
            </Panel>

            {(() => {
              // Customer Context — supplemental, customer-provided, unverified.
              // Specialists act on it for outreach; it never overrides invoice
              // facts, benchmarks or recovery figures.
              const ctx = negotiationContextFields(
                buildCustomerContextRecord(review as never),
              );
              if (!ctx.length) return null;
              return (
                <Panel title="Customer Context" icon={<StickyNote className="h-4 w-4" />}>
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Customer provided · Unverified
                  </p>
                  {ctx.map((f) => (
                    <ContactRow
                      key={f.key}
                      icon={<FileText className="h-4 w-4" />}
                      label={f.label}
                      value={f.value}
                      placeholder="Not provided"
                    />
                  ))}
                </Panel>
              );
            })()}

            <Panel title="Vendor Contact" icon={<Building2 className="h-4 w-4" />}>
              <ContactRow icon={<Building2 className="h-4 w-4" />} label="Vendor Name" value={review.vendor} placeholder="Vendor not extracted" />
              <ContactRow icon={<UserCircle2 className="h-4 w-4" />} label="Primary Contact" value={null} placeholder="Pending" />
              <ContactRow icon={<Mail className="h-4 w-4" />} label="Email" value={null} placeholder="Will populate after first outreach" />
              <ContactRow icon={<Phone className="h-4 w-4" />} label="Phone" value={null} placeholder="Pending" />
              <ContactRow icon={<FileText className="h-4 w-4" />} label="Service ZIP" value={review.zip} placeholder="Not on file" />
            </Panel>


            <Panel title="Internal Notes" icon={<StickyNote className="h-4 w-4" />}>
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                rows={3}
                placeholder="Note for the team only…"
                className="w-full resize-none rounded-md border border-border bg-card px-2 py-1.5 text-xs"
              />
              <button
                type="button"
                onClick={addNote}
                disabled={busy === "note" || !noteBody.trim()}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90 disabled:opacity-60"
              >
                {busy === "note" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                Save Note
              </button>
              <ul className="mt-3 space-y-2">
                {notes.slice(0, 5).map((n) => (
                  <li key={n.id} className="rounded-md bg-secondary/60 p-2 text-xs text-navy">
                    <div className="whitespace-pre-line">{n.body}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          </aside>

          {/* CENTER — Pinned response + Conversation + Timeline */}
          <div className="space-y-6">
            <PinnedResponse messages={messages} />

            <Panel
              title="Vendor & Customer Conversation"
              icon={<MessageSquare className="h-4 w-4" />}
            >
              <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg bg-secondary/30 p-3">
                {messages.length === 0 ? (
                  <EmptyPanel
                    icon={<MessageSquare className="h-5 w-5" />}
                    title="No vendor communication has occurred yet"
                    body="Generated letters and emails will appear here once outreach begins."
                  />
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex ${
                        m.sender_role === "staff" || m.sender_role === "admin"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs shadow-sm ${
                          m.sender_role === "staff" || m.sender_role === "admin"
                            ? "bg-negotiate text-white"
                            : "bg-card text-navy border border-border"
                        }`}
                      >
                        <div className="whitespace-pre-line">{m.body}</div>
                        <div className="mt-1 text-[10px] opacity-80">
                          {m.sender_role} ·{" "}
                          {new Date(m.created_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Quick snippets */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {SNIPPETS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() =>
                      setOutgoing(
                        s.body({
                          vendor: review.vendor ?? "Vendor",
                          fileNo: review.file_number,
                          counter: negotiation.counter_offer ?? negotiation.estimated_target ?? original,
                        }),
                      )
                    }
                    className="rounded-full border border-border bg-card px-2.5 py-1 text-[10px] font-semibold text-navy transition hover:border-negotiate/30 hover:bg-negotiate-soft hover:text-negotiate"
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <textarea
                value={outgoing}
                onChange={(e) => setOutgoing(e.target.value)}
                rows={3}
                placeholder="Draft an outgoing message — or pick a snippet above…"
                className="mt-2 w-full resize-none rounded-md border border-border bg-card px-3 py-2 text-sm"
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => sendOutgoing("email_customer")}
                  disabled={!outgoing.trim() || busy === "email_customer"}
                  className="inline-flex items-center gap-1.5 rounded-md bg-negotiate px-3 py-1.5 text-xs font-semibold text-white hover:bg-negotiate disabled:opacity-60"
                >
                  {busy === "email_customer" ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                  Send to Customer
                </button>
                <button
                  type="button"
                  onClick={() => sendOutgoing("email_vendor")}
                  disabled={!outgoing.trim() || busy === "email_vendor"}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-navy hover:bg-secondary disabled:opacity-60"
                >
                  {busy === "email_vendor" ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Mail className="h-3 w-3" />
                  )}
                  Send to Vendor
                </button>
              </div>
            </Panel>

            <Panel title="Negotiation Timeline" icon={<CircleDot className="h-4 w-4" />}>
              {timeline.length === 0 ? (
                <EmptyPanel
                  icon={<CircleDot className="h-5 w-5" />}
                  title="No events yet"
                  body="Actions from the top bar are recorded here automatically."
                />
              ) : (
                <div className="space-y-6">
                  {groupByDay(timeline).map((group, gi) => (
                    <div key={group.day}>
                      <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-navy/70">
                        <Calendar className="h-3.5 w-3.5" />
                        {group.label}
                        <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-navy/60">
                          {group.items.length}
                        </span>
                      </div>
                      <ol className="relative ml-2 border-l-2 border-negotiate/20">
                        {group.items.map((e, i) => {
                          const isNewest = gi === 0 && i === 0;
                          return (
                            <li key={e.id} className="ml-5 pb-5 last:pb-0">
                              <span
                                className={`absolute -left-[9px] mt-2 h-4 w-4 rounded-full ring-4 ring-white ${
                                  isNewest
                                    ? "bg-negotiate shadow-[0_0_0_4px_rgba(147,51,234,0.15)]"
                                    : "bg-negotiate/30"
                                }`}
                              />
                              <div
                                className={`rounded-xl p-4 transition ${
                                  isNewest
                                    ? "border-2 border-negotiate/30 bg-negotiate-soft shadow-sm"
                                    : "border border-border bg-card"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    {isNewest && (
                                      <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-negotiate px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                                        Newest
                                      </div>
                                    )}
                                    <div className="text-sm font-semibold text-navy">{e.title}</div>
                                    {e.description && (
                                      <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                        {e.description}
                                      </div>
                                    )}
                                  </div>
                                  <div className="shrink-0 text-right text-[11px] font-medium text-navy/70">
                                    {new Date(e.created_at).toLocaleTimeString(undefined, {
                                      hour: "numeric",
                                      minute: "2-digit",
                                    })}
                                  </div>
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
                                  <span className="rounded-full bg-secondary px-1.5 py-0.5 font-semibold text-navy/70">
                                    {prettyEventType(e.event_type)}
                                  </span>
                                  <span>·</span>
                                  <span>
                                    {e.visible_to_customer ? "Visible to customer" : "Internal"}
                                  </span>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  ))}
                </div>
              )}
            </Panel>


            {/* Settlement panel appears once completed; empty state otherwise */}
            {isCompleted ? (
              <Panel title="Settlement" icon={<Gavel className="h-4 w-4 text-savings" />}>
                <div className="grid gap-3 md:grid-cols-4">
                  <SettlementStat label="Original" value={`$${Math.round(original).toLocaleString()}`} />
                  <SettlementStat
                    label="Settled"
                    value={
                      settlement != null
                        ? `$${Math.round(settlement).toLocaleString()}`
                        : "—"
                    }
                  />
                  <SettlementStat
                    label="Actual Negotiated Savings"
                    value={savings != null ? `$${Math.round(savings).toLocaleString()}` : NOT_AVAILABLE}
                    accent
                  />
                  <SettlementStat
                    label="Actual Savings %"
                    value={savingsPct != null ? `${savingsPct}%` : NOT_AVAILABLE}
                    accent
                  />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <SettlementStat label="Letters Sent" value={timeline.filter((e) => e.event_type.startsWith("letter_")).length} />
                  <SettlementStat label="Calls Made" value={timeline.filter((e) => e.event_type === "phone_call").length} />
                  <SettlementStat
                    label="Days to Close"
                    value={Math.max(
                      1,
                      Math.floor(
                        ((negotiation.accepted_at
                          ? new Date(negotiation.accepted_at).getTime()
                          : Date.now()) -
                          new Date(negotiation.created_at).getTime()) /
                          86_400_000,
                      ),
                    )}
                  />
                </div>
              </Panel>
            ) : (
              <Panel title="Settlement" icon={<Gavel className="h-4 w-4" />}>
                <EmptyPanel
                  icon={<Gavel className="h-5 w-5" />}
                  title="No settlement has been recorded"
                  body="Once negotiations begin, offers and agreed savings will appear here."
                />
              </Panel>
            )}
          </div>

          {/* RIGHT — Floating Savings + Health + Builder + Quick Actions */}
          <aside className="space-y-4">
            <FloatingSavingsPanel
              original={original}
              counter={negotiation.counter_offer}
              target={negotiation.estimated_target}
              settlement={settlement}
              potential={potentialSavings}
              potentialLabel={financial.label}
              potentialKind={financial.kind}
            />
            <HealthScorePanel
              negotiation={negotiation}
              messages={messages}
              filesCount={files.length}
            />

            <Panel title="Negotiation Builder" icon={<Sparkles className="h-4 w-4" />}>
              {/* — Strategy — */}
              <BuilderGroup label="Strategy">
                <BuilderField label="Strongest Argument">
                  <textarea
                    value={negotiation.strongest_argument ?? ""}
                    onChange={(e) =>
                      setNegotiation({ ...negotiation, strongest_argument: e.target.value })
                    }
                    onBlur={(e) => updateNeg({ strongest_argument: e.target.value })}
                    rows={2}
                    className="w-full resize-none rounded-md border border-border bg-card px-2.5 py-2 text-xs"
                    placeholder="e.g. Charges lack supporting documentation."
                  />
                </BuilderField>

                <BuilderField label="Recommended Tone">
                  <div className="flex flex-wrap gap-1.5">
                    {TONES.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => updateNeg({ tone: t.key })}
                        className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${
                          negotiation.tone === t.key
                            ? "border-negotiate bg-negotiate text-white"
                            : "border-border bg-card text-navy hover:bg-secondary"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </BuilderField>

                <BuilderField label="Priority">
                  <div className="flex flex-wrap gap-1.5">
                    {PRIORITIES.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => updateNeg({ priority: p.key })}
                        className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${
                          negotiation.priority === p.key
                            ? "border-navy bg-navy text-white"
                            : "border-border bg-card text-navy hover:bg-secondary"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </BuilderField>
              </BuilderGroup>

              {/* — Financial Goal — */}
              <BuilderGroup label="Financial Goal">
                <div className="grid grid-cols-2 gap-3">
                  <BuilderField label="Counter Offer">
                    <input
                      type="number"
                      defaultValue={negotiation.counter_offer ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        updateNeg({ counter_offer: v });
                      }}
                      className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-xs"
                      placeholder="$"
                    />
                  </BuilderField>
                  <BuilderField label="Target Settlement">
                    <input
                      type="number"
                      defaultValue={negotiation.estimated_target ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value ? Number(e.target.value) : null;
                        updateNeg({ estimated_target: v });
                      }}
                      className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-xs"
                      placeholder="$"
                    />
                  </BuilderField>
                </div>

                <div className="mt-2 rounded-md border border-negotiate/20 bg-negotiate-soft p-2.5 text-[11px] text-navy">
                  <div className="flex items-center gap-1.5 font-semibold">
                    <Target className="h-3 w-3 text-negotiate" /> Estimated success
                  </div>
                  <div className="mt-0.5 text-muted-foreground">
                    {negotiation.strongest_argument && negotiation.counter_offer
                      ? "High — clear argument and defined counter."
                      : "Medium — add a strongest argument and counter offer."}
                  </div>
                </div>
              </BuilderGroup>

              {/* — Letters — */}
              <BuilderGroup label="Letters">
                <div className="grid grid-cols-2 gap-2">
                  <BuilderBtn onClick={() => generateLetter("initial")} busy={busy === "gen-initial"}>
                    Initial
                  </BuilderBtn>
                  <BuilderBtn onClick={() => generateLetter("followup")} busy={busy === "gen-followup"}>
                    Follow Up
                  </BuilderBtn>
                  <BuilderBtn onClick={() => generateLetter("final")} busy={busy === "gen-final"}>
                    Final Demand
                  </BuilderBtn>
                  <BuilderBtn onClick={() => generateLetter("settlement")} busy={busy === "gen-settlement"}>
                    Settlement
                  </BuilderBtn>
                  <BuilderBtn onClick={() => generateLetter("closure")} busy={busy === "gen-closure"}>
                    Closure
                  </BuilderBtn>
                </div>
              </BuilderGroup>

              {saving && (
                <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </div>
              )}
            </Panel>

            <Panel title="Quick Actions" icon={<ShieldCheck className="h-4 w-4" />}>
              <QuickBtn icon={<Bell className="h-3.5 w-3.5" />} onClick={() => toast.info("Reminder queued")}>
                Send Reminder
              </QuickBtn>
              <QuickBtn icon={<Calendar className="h-3.5 w-3.5" />} onClick={() => toast.info("Scheduler opens in V1.1")}>
                Schedule Call
              </QuickBtn>
              <QuickBtn icon={<FileSignature className="h-3.5 w-3.5" />} onClick={() => generateLetter("followup")}>
                Generate New Letter
              </QuickBtn>
              <QuickBtn icon={<Upload className="h-3.5 w-3.5" />} onClick={() => toast.info("Upload UI in V1.1")}>
                Upload Settlement
              </QuickBtn>
              <QuickBtn icon={<CheckCircle2 className="h-3.5 w-3.5" />} onClick={() => toast.info("Approval requested")}>
                Request Approval
              </QuickBtn>
              <QuickBtn icon={<AlertTriangle className="h-3.5 w-3.5 text-orange-600" />} onClick={() => updateNeg({ priority: "urgent" })}>
                Escalate
              </QuickBtn>
              <QuickBtn icon={<TrendingUp className="h-3.5 w-3.5 text-savings" />} onClick={() => transition("completed", { title: "Negotiation closed", reviewStatus: "completed" })}>
                Close Negotiation
              </QuickBtn>
            </Panel>

            <Panel title={`Documents (${files.length})`} icon={<FileText className="h-4 w-4" />}>
              {files.length === 0 ? (
                <EmptyPanel
                  icon={<FileText className="h-5 w-5" />}
                  title="No documents"
                  body="Vendor invoices, photos, and settlement paperwork uploaded to this file will appear here."
                />

              ) : (
                <ul className="space-y-1.5">
                  {files.slice(0, 8).map((f) => (
                    <li
                      key={f.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2 py-1.5 text-[11px]"
                    >
                      <span className="truncate text-navy">{f.file_name}</span>
                      <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                        {f.kind ?? "file"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </aside>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

/* ─────────── UI primitives ─────────── */

function MiniStat({
  label,
  value,
  positive,
}: {
  label: string;
  value: React.ReactNode;
  positive?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3 text-right ${
        positive ? "border-savings/30 bg-savings-soft" : "border-border bg-secondary/40"
      }`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-1 font-display text-lg ${positive ? "text-savings" : "text-navy"}`}
      >
        {value}
      </div>
    </div>
  );
}

function Panel({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        {icon}
        {title}
      </div>
      <div className="mt-3 space-y-1.5">{children}</div>
    </section>
  );
}

function Row({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-navy">
      <span className="text-muted-foreground">{icon}</span>
      <span className="truncate">{children}</span>
    </div>
  );
}

function ContactRow({
  icon,
  label,
  value,
  placeholder,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode | null | undefined;
  placeholder: string;
}) {
  const hasValue = value != null && value !== "";
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div
          className={
            hasValue
              ? "mt-0.5 truncate text-sm font-medium text-navy"
              : "mt-0.5 truncate text-xs italic text-muted-foreground/70"
          }
        >
          {hasValue ? value : placeholder}
        </div>
      </div>
    </div>
  );
}

function ActionButton({
  onClick,
  children,
  icon,
  busy,
  primary,
  variant = "secondary",
}: {
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
  busy?: boolean;
  primary?: boolean;
  variant?: "secondary" | "subtle";
}) {
  const cls = primary
    ? "bg-negotiate text-white shadow-sm hover:bg-negotiate"
    : variant === "subtle"
      ? "border border-transparent bg-transparent text-navy/80 hover:bg-secondary"
      : "border border-border bg-card text-navy hover:bg-secondary";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition disabled:opacity-60 ${cls}`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
      {children}
    </button>
  );
}

function BuilderBtn({
  onClick,
  busy,
  children,
}: {
  onClick: () => void;
  busy?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center justify-center gap-1 rounded-md border border-border bg-card px-2 py-1.5 text-[10px] font-semibold text-navy hover:bg-secondary disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSignature className="h-3 w-3" />}
      {children}
    </button>
  );
}

function BuilderGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-5 border-t border-border pt-4 first:mt-0 first:border-t-0 first:pt-0">
      <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-negotiate">
        {label}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function BuilderField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

function QuickBtn({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 inline-flex w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-navy hover:bg-secondary"
    >
      {icon}
      {children}
    </button>
  );
}

function SettlementStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        accent ? "border-savings/30 bg-savings-soft" : "border-border bg-secondary/40"
      }`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 font-display text-lg ${accent ? "text-savings" : "text-navy"}`}>
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-negotiate/20 bg-negotiate-soft px-2.5 py-0.5 text-[10px] font-semibold text-negotiate">
      <CircleDot className="h-3 w-3" />
      {status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

/* ─────────── Snippets ─────────── */

const SNIPPETS: Array<{
  key: string;
  label: string;
  body: (ctx: { vendor: string; fileNo: string; counter: number | null }) => string;
}> = [
  {
    key: "request_docs",
    label: "Request documentation",
    body: ({ vendor, fileNo }) =>
      `Dear ${vendor},\n\nPer our review of invoice ${fileNo}, please provide itemized documentation for each charge listed, including labor rates, time logs, and any supporting photos. We require this to validate the invoice before payment.\n\nRegards,\nClaimGuard`,
  },
  {
    key: "followup",
    label: "Follow up",
    body: ({ vendor, fileNo }) =>
      `Dear ${vendor},\n\nFollowing up on our earlier correspondence regarding invoice ${fileNo}. Please confirm receipt and provide your response within 3 business days.\n\nRegards,\nClaimGuard`,
  },
  {
    key: "counter",
    label: "Counteroffer",
    body: ({ vendor, fileNo, counter }) =>
      `Dear ${vendor},\n\nBased on our review of invoice ${fileNo}, we propose a counteroffer of $${(counter ?? 0).toLocaleString()} as full settlement of the disputed charges. Please confirm within 5 business days.\n\nRegards,\nClaimGuard`,
  },
  {
    key: "final",
    label: "Final demand",
    body: ({ vendor, fileNo }) =>
      `FINAL DEMAND — ${fileNo}\n\nDear ${vendor},\n\nThis is our final written attempt to resolve the disputed charges on invoice ${fileNo}. Absent a response within 5 business days, we will advise our client of all available remedies.\n\nRegards,\nClaimGuard`,
  },
  {
    key: "settlement_confirm",
    label: "Settlement confirmation",
    body: ({ vendor, fileNo, counter }) =>
      `Dear ${vendor},\n\nConfirming settlement of invoice ${fileNo} at $${(counter ?? 0).toLocaleString()}, in full and final resolution of all disputed line items. Please issue a revised invoice for our records.\n\nRegards,\nClaimGuard`,
  },
];

/* ─────────── Timeline grouping ─────────── */

function groupByDay(items: TimelineEvent[]) {
  const map = new Map<string, TimelineEvent[]>();
  for (const it of items) {
    const d = new Date(it.created_at);
    const key = d.toISOString().slice(0, 10);
    const arr = map.get(key) ?? [];
    arr.push(it);
    map.set(key, arr);
  }
  const today = new Date().toISOString().slice(0, 10);
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = yesterdayDate.toISOString().slice(0, 10);
  return [...map.entries()].map(([day, items]) => ({
    day,
    label:
      day === today
        ? "Today"
        : day === yesterday
          ? "Yesterday"
          : new Date(day).toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            }),
    items,
  }));
}

/* ─────────── Pinned response ─────────── */

function PinnedResponse({ messages }: { messages: NegotiationMessage[] }) {
  const latestVendor = messages.find((m) => m.sender_role !== "staff" && m.sender_role !== "admin");
  if (!latestVendor) {
    return (
      <section className="rounded-2xl border border-dashed border-border bg-card p-5 shadow-card">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-navy/70">
            <MessageSquare className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Latest Vendor Response
            </div>
            <div className="mt-1 text-sm font-semibold text-navy">No vendor response yet</div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Once the vendor replies, the newest message will pin here so you never miss it.
            </p>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="rounded-2xl border-2 border-negotiate/30 bg-negotiate-soft p-5 shadow-card">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-negotiate text-white">
          <MessageSquare className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-negotiate">
              Latest Vendor Response — Pinned
            </div>
            <div className="text-[10px] text-muted-foreground">
              {new Date(latestVendor.created_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          </div>
          <div className="mt-1 whitespace-pre-line text-sm text-navy">{latestVendor.body}</div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Floating savings ─────────── */

function FloatingSavingsPanel({
  original,
  counter,
  target,
  settlement,
  potential,
  potentialLabel,
  potentialKind,
}: {
  original: number;
  counter: number | null;
  target: number | null;
  settlement: number | null;
  potential: number | null;
  potentialLabel: string;
  potentialKind: "actual_settlement" | "supported_recovery" | "unavailable";
}) {
  const currentSavings =
    settlement != null ? actualNegotiatedSavings(original || null, settlement) : potential;
  const pct =
    currentSavings != null && original > 0 ? Math.round((currentSavings / original) * 100) : null;
  const heroLabel = settlement != null ? "Actual Negotiated Savings" : potentialLabel;
  return (
    <section className="rounded-2xl border-2 border-savings/40 bg-gradient-to-br from-savings-soft to-white p-5 shadow-card">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-savings">
        <TrendingUp className="h-4 w-4" />
        Savings Summary
      </div>

      {/* Hero: Projected Savings */}
      <div className="mt-4 rounded-xl border border-savings/30 bg-card p-4 shadow-sm">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {heroLabel}
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="font-display text-4xl font-bold leading-none text-savings">
            {currentSavings != null
              ? `$${Math.round(currentSavings).toLocaleString()}`
              : NOT_AVAILABLE}
          </div>
        </div>
        {pct != null && potentialKind !== "unavailable" && (
          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-savings/10 px-2 py-0.5 text-[11px] font-bold text-savings">
            <TrendingUp className="h-3 w-3" />
            {pct}% off invoice
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="mt-4 space-y-2 text-xs">
        <SavingsRow label="Original Invoice" value={original} placeholder="Not yet available" />
        <SavingsRow label="Current Vendor Offer" value={counter} placeholder="Awaiting vendor" />
        <SavingsRow label="Target Settlement" value={target} placeholder="To be defined" />
        <div className="my-1 h-px bg-savings/20" />
        <SavingsRow
          label="Final Settlement Amount"
          value={settlement}
          placeholder="Not yet settled"
          bold
        />
      </div>
    </section>
  );
}

function SavingsRow({
  label,
  value,
  placeholder = "—",
  bold,
}: {
  label: string;
  value: number | null;
  placeholder?: string;
  bold?: boolean;
}) {
  const hasValue = value != null && !Number.isNaN(value);
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={
          hasValue
            ? `${bold ? "font-bold" : "font-semibold"} text-navy`
            : "text-xs italic text-muted-foreground/70"
        }
      >
        {hasValue ? `$${Math.round(value).toLocaleString()}` : placeholder}
      </span>
    </div>
  );
}

/* ─────────── Health score ─────────── */

function HealthScorePanel({
  negotiation,
  messages,
  filesCount,
}: {
  negotiation: Negotiation;
  messages: NegotiationMessage[];
  filesCount: number;
}) {
  const evidenceStrength: HealthLevel = filesCount >= 3 ? "strong" : filesCount >= 1 ? "medium" : "weak";
  const vendorReplied = messages.some((m) => m.sender_role !== "staff" && m.sender_role !== "admin");
  const vendorStatus: HealthLevel = vendorReplied
    ? "strong"
    : ["vendor_contacted", "waiting_response", "waiting_vendor"].includes(negotiation.status)
      ? "medium"
      : "weak";
  const daysOpen = Math.floor(
    (Date.now() - new Date(negotiation.created_at).getTime()) / 86_400_000,
  );
  const daysStatus: HealthLevel = daysOpen <= 1 ? "strong" : daysOpen <= 4 ? "medium" : "weak";

  const nextAction = computeNextAction(negotiation, messages);

  const score = Math.round(
    ((toScore(evidenceStrength) + toScore(vendorStatus) + toScore(daysStatus)) / 9) * 100,
  );
  const health =
    score >= 80
      ? { label: "Excellent", tone: "text-savings", bg: "bg-savings/10", bar: "bg-savings" }
      : score >= 60
        ? { label: "Healthy", tone: "text-emerald-700", bg: "bg-emerald-50", bar: "bg-emerald-500" }
        : score >= 40
          ? { label: "Needs Attention", tone: "text-amber-700", bg: "bg-amber-50", bar: "bg-amber-500" }
          : { label: "Critical", tone: "text-red-700", bg: "bg-red-50", bar: "bg-red-500" };

  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          <ShieldCheck className="h-4 w-4" />
          Health Score
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${health.tone} ${health.bg}`}
        >
          {health.label}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-display text-3xl font-bold text-navy">{score}</div>
        <div className="text-xs text-muted-foreground">/ 100</div>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
        <div
          className={`h-full rounded-full transition-all ${health.bar}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <ul className="mt-4 space-y-2 text-xs">
        <HealthRow label="Evidence strength" level={evidenceStrength} />
        <HealthRow label="Vendor response" level={vendorStatus} />
        <HealthRow label={`Days open (${daysOpen}d)`} level={daysStatus} />
      </ul>
      <div className="mt-3 rounded-md border border-negotiate/20 bg-negotiate-soft p-2.5 text-[11px]">
        <div className="flex items-center gap-1.5 font-semibold text-negotiate">
          <AlertTriangle className="h-3 w-3" /> Next action
        </div>
        <div className="mt-0.5 text-navy">{nextAction}</div>
      </div>
    </section>
  );
}

type HealthLevel = "strong" | "medium" | "weak";

function toScore(l: HealthLevel) {
  return l === "strong" ? 3 : l === "medium" ? 2 : 1;
}

function HealthRow({ label, level }: { label: string; level: HealthLevel }) {
  const cls =
    level === "strong"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : level === "medium"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-red-50 text-red-700 border-red-200";
  const dot = level === "strong" ? "bg-emerald-500" : level === "medium" ? "bg-amber-500" : "bg-red-500";
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-navy">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        {label}
      </span>
      <span
        className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${cls}`}
      >
        {level}
      </span>
    </li>
  );
}

function computeNextAction(n: Negotiation, messages: NegotiationMessage[]): string {
  if (["completed", "accepted"].includes(n.status)) return "Negotiation complete — archive when ready.";
  if (n.status === "offer_received") return "Review vendor offer and respond within 24h.";
  if (!n.assigned_specialist_id) return "Assign a specialist today.";
  if (["vendor_contacted", "waiting_response", "waiting_vendor"].includes(n.status)) {
    const last = messages[0];
    const daysSince = last
      ? Math.floor((Date.now() - new Date(last.created_at).getTime()) / 86_400_000)
      : 0;
    if (daysSince >= 2) return `Vendor follow-up overdue (${daysSince}d silent).`;
    return "Awaiting vendor response.";
  }
  const last = messages[0];
  if (last?.sender_role === "customer") return "Reply to customer message.";
  return "Draft next vendor communication.";
}

/* ─────────── Empty panel ─────────── */

function EmptyPanel({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-border bg-secondary/20 px-4 py-6 text-center">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-card text-muted-foreground">
        {icon}
      </div>
      <div className="text-sm font-semibold text-navy">{title}</div>
      <p className="max-w-xs text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

/* ─────────── Case Summary ─────────── */

function CaseSummaryPanel({
  negotiation,
  review,
  customer,
  messages,
  timelineCount,
}: {
  negotiation: Negotiation;
  review: Review;
  customer: Profile | null;
  messages: NegotiationMessage[];
  timelineCount: number;
}) {
  const vendorReplied = messages.some(
    (m) => m.sender_role !== "staff" && m.sender_role !== "admin",
  );
  const parts: string[] = [];
  parts.push(
    `Invoice ${review.file_number}${review.vendor ? ` from ${review.vendor}` : ""} submitted for review.`,
  );
  if (customer?.full_name) parts.push(`${customer.full_name} requested Expert Negotiation.`);
  else parts.push("Customer has requested Expert Negotiation.");

  if (["completed", "accepted", "settled"].includes(negotiation.status)) {
    parts.push("Negotiation is complete and ready to archive.");
  } else if (negotiation.status === "offer_received") {
    parts.push("Vendor has sent an offer awaiting specialist review.");
  } else if (vendorReplied) {
    parts.push("Vendor has replied — response requires review.");
  } else if (
    ["vendor_contacted", "waiting_response", "waiting_vendor"].includes(negotiation.status)
  ) {
    parts.push("Vendor has been contacted and we are awaiting a response.");
  } else if (!negotiation.assigned_specialist_id) {
    parts.push("Vendor has not yet been contacted. Recommended next action: assign specialist.");
  } else {
    parts.push("Specialist is preparing outreach to the vendor.");
  }

  const summary = parts.join(" ");

  return (
    <section className="rounded-2xl border border-negotiate/20 bg-gradient-to-r from-negotiate-soft via-white to-white p-5 shadow-card">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-negotiate-soft text-negotiate">
          <FileText className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-negotiate">
              Case Summary
            </div>
            <div className="text-[10px] text-muted-foreground">
              {timelineCount} event{timelineCount === 1 ? "" : "s"} on file
            </div>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-navy">{summary}</p>
        </div>
      </div>
    </section>
  );
}

function prettyEventType(t: string) {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
